import { apiRequest, resolveApiLocale } from './client'
import { SILKREELS_API_BASE, TENANT_HEADER } from './config'
import { getActiveTenantCode } from '@/shared/lib/tenant-code'
import { ensureFreshAccessToken, readAccessToken } from './authSession'
import type {
  Script,
  ScriptComment,
  ScriptDocument,
  ScriptDocumentList,
  ScriptEpisodeList,
  ScriptRevision,
  StudioScriptListing,
} from './studio-production'

const WRITER_SCRIPT_BASE = '/api/v1/studio/writer/scripts'

function writerEpisodePath(scriptId: number, episodeNo: number): string {
  return `${WRITER_SCRIPT_BASE}/${scriptId}/episodes/${episodeNo}`
}

export const WRITER_STUDIO_BASE = '/studio/writer'

export function listWriterScriptDocuments(limit = 20, offset = 0): Promise<ScriptDocumentList> {
  return apiRequest(`${WRITER_SCRIPT_BASE}?limit=${limit}&offset=${offset}`)
}

export function createWriterScriptDocument(body: {
  title?: string
  cover_url?: string
  summary?: string
  episode_target?: number
  episodes_per_season?: number
  project_type?: string
  default_locale?: string
}): Promise<ScriptDocument> {
  return apiRequest(WRITER_SCRIPT_BASE, { method: 'POST', body })
}

export function getWriterScriptDocument(scriptId: number): Promise<ScriptDocument> {
  return apiRequest(`${WRITER_SCRIPT_BASE}/${scriptId}`)
}

export function patchWriterScriptDocument(
  scriptId: number,
  body: Record<string, unknown>,
): Promise<ScriptDocument> {
  return apiRequest(`${WRITER_SCRIPT_BASE}/${scriptId}`, { method: 'PATCH', body })
}

export function deleteWriterScriptDocument(scriptId: number): Promise<{ ok: boolean }> {
  return apiRequest(`${WRITER_SCRIPT_BASE}/${scriptId}`, { method: 'DELETE' })
}

export function listWriterScriptEpisodes(scriptId: number): Promise<ScriptEpisodeList> {
  return apiRequest(`${WRITER_SCRIPT_BASE}/${scriptId}/episodes`)
}

export function getWriterScript(scriptId: number, episodeNo: number, locale?: string): Promise<Script> {
  const q = locale ? `?locale=${encodeURIComponent(locale)}` : ''
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}${q}`)
}

export function saveWriterScript(
  scriptId: number,
  episodeNo: number,
  body: {
    body?: string
    title?: string
    outline?: string
    translations?: Record<string, Record<string, string>>
  },
): Promise<Script> {
  return apiRequest(writerEpisodePath(scriptId, episodeNo), { method: 'PUT', body })
}

export function deleteWriterScriptEpisode(scriptId: number, episodeNo: number): Promise<{ ok: boolean }> {
  return apiRequest(writerEpisodePath(scriptId, episodeNo), { method: 'DELETE' })
}

export function writerBasePath(): string {
  return WRITER_STUDIO_BASE
}

export function parseWriterScript(scriptId: number, episodeNo: number): Promise<Script> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/parse`, { method: 'POST' })
}

export function finalizeWriterScript(
  scriptId: number,
  episodeNo: number,
  changeNote?: string,
): Promise<Script> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/finalize`, {
    method: 'POST',
    body: changeNote ? { change_note: changeNote } : {},
  })
}

export function listWriterScriptRevisions(
  scriptId: number,
  episodeNo: number,
): Promise<{ items: ScriptRevision[] }> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/revisions`)
}

export function publishWriterScriptRevision(
  scriptId: number,
  episodeNo: number,
  changeNote?: string,
): Promise<Script> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/revisions/publish`, {
    method: 'POST',
    body: changeNote ? { change_note: changeNote } : {},
  })
}

export function lockWriterScriptRevision(
  scriptId: number,
  episodeNo: number,
  revisionNo: number,
): Promise<Script> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/revisions/lock`, {
    method: 'POST',
    body: { revision_no: revisionNo },
  })
}

export function listWriterScriptComments(
  scriptId: number,
  episodeNo: number,
): Promise<{ items: ScriptComment[]; total?: number }> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/comments`)
}

export function listWriterScriptDocumentComments(
  scriptId: number,
  opts?: { limit?: number; offset?: number },
): Promise<{ items: ScriptComment[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  if (opts?.limit != null) params.set('limit', String(opts.limit))
  if (opts?.offset != null) params.set('offset', String(opts.offset))
  const q = params.toString()
  return apiRequest(`${WRITER_SCRIPT_BASE}/${scriptId}/comments${q ? `?${q}` : ''}`)
}

export function createWriterScriptComment(
  scriptId: number,
  episodeNo: number,
  body: {
    body: string
    scene_no?: number
    anchor_start?: number
    anchor_end?: number
    anchor_quote?: string
  },
): Promise<ScriptComment> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/comments`, { method: 'POST', body })
}

export function resolveWriterScriptComment(
  scriptId: number,
  episodeNo: number,
  commentId: string,
  resolved: boolean,
): Promise<ScriptComment> {
  return apiRequest(
    `${writerEpisodePath(scriptId, episodeNo)}/comments/${encodeURIComponent(commentId)}`,
    { method: 'PATCH', body: { resolved } },
  )
}

export function createWriterScriptListing(
  scriptId: number,
  episodeNo: number,
  body: {
    title?: string
    summary?: string
    cover_url?: string
    price_points: number
    license_mode?: 'non_exclusive' | 'exclusive'
  },
): Promise<StudioScriptListing> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/listings`, { method: 'POST', body })
}

export function applyWriterScriptAI(
  scriptId: number,
  episodeNo: number,
  body: {
    prompt?: string
    model_id?: string
    text?: string
    selection_start?: number
    selection_end?: number
    scene_no?: number
    preview?: boolean
  },
): Promise<{ text: string; applied: boolean; cost?: number; script?: Script }> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/ai/apply`, {
    method: 'POST',
    body,
  })
}

export function suggestWriterScriptAI(
  scriptId: number,
  episodeNo: number,
  body: { prompt: string; model_id?: string; context?: string; scene_no?: number },
): Promise<{ text: string; applied: boolean; cost?: number }> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/ai/suggest`, {
    method: 'POST',
    body,
  })
}

export type WriterScriptAgentSkill =
  | 'series_outline'
  | 'story_content'
  | 'episode_outline'
  | 'continue_scene'
  | 'episode_scene_list'
  | 'polish'
  | 'expand_outline'
  | 'continuity_check'
  | 'freeform'

export type WriterAgentLayer = 'series' | 'story' | 'episode'

export type WriterAgentOutputKind =
  | 'script'
  | 'series_outline'
  | 'story_content'
  | 'episode_outline'
  | 'scene_list'
  | 'next_scene'
  | 'analysis'

export function runWriterScriptAgent(
  scriptId: number,
  episodeNo: number,
  body: {
    skill?: WriterScriptAgentSkill
    layer?: WriterAgentLayer
    message: string
    model_id?: string
    context?: string
    scene_no?: number
    selection_start?: number
    selection_end?: number
    history?: Array<{ role: 'user' | 'assistant'; content: string }>
  },
): Promise<{ task_id: string; skill: string; label: string; output_kind?: WriterAgentOutputKind; cost?: number; writable: boolean }> {
  return apiRequest(`${writerEpisodePath(scriptId, episodeNo)}/ai/agent`, {
    method: 'POST',
    body,
  })
}

export async function downloadWriterScriptExport(
  scriptId: number,
  episodeNo: number,
  format: 'fountain' | 'pdf' | 'fdx' = 'fountain',
  convention?: 'cn' | 'hollywood',
): Promise<void> {
  await ensureFreshAccessToken()
  const params = new URLSearchParams()
  params.set('format', format)
  if (convention) params.set('convention', convention)
  const res = await fetch(
    `${SILKREELS_API_BASE}${writerEpisodePath(scriptId, episodeNo)}/export?${params}`,
    {
      headers: {
        Authorization: `Bearer ${readAccessToken()}`,
        [TENANT_HEADER]: getActiveTenantCode(),
        'Accept-Language': resolveApiLocale(),
      },
      credentials: 'include',
    },
  )
  if (!res.ok) throw new Error('export failed')
  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename=([^;]+)/i.exec(disposition)
  const filename =
    match?.[1]?.trim() ?? (format === 'pdf' ? 'script.pdf' : format === 'fdx' ? 'script.fdx' : 'script.fountain')
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
